import {
  type ManifestVersionData,
  getCommitTimestamp,
} from '@canvas/buildtime-constants';
import {
  EventClassification,
  CustomerImpact,
  captureException,
  CapabilityTier,
} from '@canvas/sentry-react';

const CHECK_INTERVAL_MS = 5 * 60_000; // 5 minutes

/**
 * This checks the current bundle version of canvas in the users browser
 * on an interval. If the version exceeds to maximum allowed in the manifest.json
 * It executes onStaleBundle (e.g. showing a notification to refresh their browser tab).
 */
export class RefreshBundlePoller {
  private timerId: number | null = null;
  private readonly onStaleBundle: () => void | Promise<void>;
  private readonly manifestSupplier: () => Promise<ManifestVersionData>;

  public constructor({
    onStaleBundle,
    manifestSupplier,
  }: {
    onStaleBundle: VoidFunction;
    manifestSupplier: () => Promise<ManifestVersionData>;
  }) {
    this.onStaleBundle = onStaleBundle;
    this.manifestSupplier = manifestSupplier;
  }

  public start = (interval: number = CHECK_INTERVAL_MS) => {
    if (this.timerId === null) {
      this.timerId = setInterval(
        () => this.checkBundleVersion(),
        interval,
      ) as unknown as number;
    }
  };

  public stop = () => {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  };

  public checkBundleVersion = async () => {
    try {
      const currCommitTimestamp = getCommitTimestamp();

      if (!currCommitTimestamp) {
        const error = new Error('Commit timestamp is missing or zero in manifest.json');
        captureException(error, {
          tags: {
            captureLocation: 'missing-commit-timestamp',
            eventClassification: EventClassification.BUG,
            /**
             * _Technically_ this could affect customers _if_ we need to tell the user to
             * refresh their browser as we use this area of the code to trigger snack-bars telling the
             * user to refresh. Ultimately however, that's a tool used only by the Fabric team so decided to go
             * with INTERNAL_TEAM for the customer impact here
             */
            customerImpact: CustomerImpact.INTERNAL_TEAM,
            capabilityTier: CapabilityTier.NONE,
          },
        });
        return;
      }

      const data: ManifestVersionData = await this.manifestSupplier();

      if (currCommitTimestamp < data.oldestSupportedCommitTimestamp) {
        await this.onStaleBundle();
      }
    } catch (error) {
      captureException(error, {
        tags: {
          captureLocation: 'checkBundleVersion-catch-all',
          customerImpact: CustomerImpact.INTERNAL_TEAM,
          capabilityTier: CapabilityTier.NONE,
        },
      });
    }
  };
}
